/**
 * KRA Photo Journal – Login Page (invite-based)
 *
 * Only users with an invite code (issued by an admin in kra-admin at
 * `staff.kimonorentalann.com`) can get in. The page auto-picks the code
 * from `/invite/:code` URL if present.
 *
 * Flow:
 *   1. enter-code  – type / paste the 4–8 char invite code → validate
 *   2. enter-label – name this device ("iPhone Linh", "iPad shop")
 *   3. redeem      – POST /api/auth/redeem-invite → receive JWT + role
 */

import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const API_BASE = (import.meta.env["VITE_API_BASE_URL"] as string | undefined) ?? "/api";

type Screen = "enter-code" | "enter-label";

interface LoginPageProps {
  onLogin: (
    token: string,
    name: string,
    extras?: { role?: string | null; staffId?: string | null }
  ) => void;
}

interface ValidateInviteResponse {
  valid: boolean;
  label?: string | null;
  role?: string;
  error?: string;
}

interface RedeemInviteResponse {
  token: string;
  name: string;
  role?: string;
  staffId?: string | null;
}

/** Read invite code from `/invite/XXXX` in the URL, if present. */
function readInviteFromUrl(): string | null {
  const match = window.location.pathname.match(/^\/invite\/([A-Za-z0-9]+)\/?$/);
  return match?.[1] ?? null;
}

function cleanUrlAfterRead() {
  if (window.location.pathname.startsWith("/invite/")) {
    window.history.replaceState({}, "", "/");
  }
}

export function LoginPage({ onLogin }: LoginPageProps) {
  const [screen, setScreen] = useState<Screen>("enter-code");
  const [code, setCode] = useState<string>("");
  const [suggestedLabel, setSuggestedLabel] = useState<string>("");
  const [deviceLabel, setDeviceLabel] = useState<string>("");
  const [roleHint, setRoleHint] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Auto-read `/invite/:code` and kick off validation on mount.
  useEffect(() => {
    const fromUrl = readInviteFromUrl();
    if (fromUrl) {
      setCode(fromUrl.toUpperCase());
      cleanUrlAfterRead();
      void validateCode(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateCode = useCallback(async (rawCode: string) => {
    const trimmed = rawCode.trim().toUpperCase();
    if (!trimmed) {
      setError("Nhập mã mời");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const resp = await fetch(`${API_BASE}/auth/validate-invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: trimmed }),
      });
      const data = (await resp.json()) as ValidateInviteResponse;
      if (!resp.ok || !data.valid) {
        setError(data.error ?? "Mã mời không hợp lệ");
        return;
      }
      setSuggestedLabel(data.label ?? "");
      setDeviceLabel(data.label ?? "");
      setRoleHint(data.role ?? null);
      setScreen("enter-label");
    } catch {
      setError("Không kết nối được máy chủ");
    } finally {
      setBusy(false);
    }
  }, []);

  const submitCode = useCallback(() => {
    void validateCode(code);
  }, [code, validateCode]);

  const submitLabel = useCallback(async () => {
    const label = deviceLabel.trim();
    if (label.length < 2) {
      setError("Tên thiết bị phải có ít nhất 2 ký tự");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const resp = await fetch(`${API_BASE}/auth/redeem-invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: code.trim().toUpperCase(), deviceLabel: label }),
      });
      const data = (await resp.json()) as RedeemInviteResponse & { error?: string };
      if (!resp.ok || !data.token) {
        setError(data.error ?? "Không đăng ký được thiết bị");
        setBusy(false);
        return;
      }
      onLogin(data.token, data.name, {
        role: data.role ?? null,
        staffId: data.staffId ?? null,
      });
    } catch {
      setError("Không kết nối được máy chủ");
      setBusy(false);
    }
  }, [code, deviceLabel, onLogin]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0f0f1e] via-[#1a1a2e] to-[#16213e] px-4">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-sm rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 p-8 shadow-2xl"
      >
        <div className="text-center mb-7">
          <h1 className="text-white text-xl font-semibold">Photo Journal</h1>
          <p className="text-white/50 text-sm mt-1">
            {screen === "enter-code" ? "Nhập mã mời để vào ứng dụng" : "Đặt tên cho thiết bị này"}
          </p>
        </div>

        <AnimatePresence mode="wait">
          {screen === "enter-code" && (
            <motion.div
              key="code"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitCode();
                }}
                placeholder="VÍ DỤ: ABCD"
                autoFocus
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                className="w-full text-center text-2xl font-mono tracking-[0.3em] uppercase rounded-2xl bg-white/10 border border-white/15 px-4 py-4 text-white placeholder:text-white/25 outline-none focus:border-white/40 transition-colors"
              />
              <button
                onClick={submitCode}
                disabled={busy || code.trim().length < 3}
                className="w-full rounded-2xl bg-white text-[#0f0f1e] font-semibold py-3 disabled:opacity-30 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
              >
                {busy ? "Đang kiểm tra…" : "Tiếp tục"}
              </button>
              <p className="text-xs text-white/40 text-center leading-relaxed">
                Chưa có mã mời? Liên hệ admin tại{" "}
                <span className="text-white/60">staff.kimonorentalann.com</span>
              </p>
            </motion.div>
          )}

          {screen === "enter-label" && (
            <motion.div
              key="label"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              {roleHint && (
                <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-xs text-white/60 text-center">
                  Vai trò: <span className="text-white/90 font-medium">{roleHint}</span>
                  {suggestedLabel && (
                    <>
                      <span className="mx-2 opacity-40">·</span>
                      Gợi ý: <span className="text-white/90 font-medium">{suggestedLabel}</span>
                    </>
                  )}
                </div>
              )}
              <input
                type="text"
                value={deviceLabel}
                onChange={(e) => setDeviceLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void submitLabel();
                }}
                placeholder="iPhone Linh, iPad shop, …"
                autoFocus
                className="w-full rounded-2xl bg-white/10 border border-white/15 px-4 py-3 text-white placeholder:text-white/30 outline-none focus:border-white/40 transition-colors"
              />
              <button
                onClick={() => void submitLabel()}
                disabled={busy || deviceLabel.trim().length < 2}
                className="w-full rounded-2xl bg-white text-[#0f0f1e] font-semibold py-3 disabled:opacity-30 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
              >
                {busy ? "Đang đăng ký…" : "Đăng nhập"}
              </button>
              <button
                onClick={() => {
                  setScreen("enter-code");
                  setError(null);
                }}
                className="w-full text-xs text-white/50 hover:text-white/80 transition-colors py-1"
              >
                ‹ Đổi mã mời khác
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {error && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-4 text-center text-sm text-red-400"
          >
            {error}
          </motion.p>
        )}
      </motion.div>
    </div>
  );
}
